import React from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Sparkles } from "lucide-react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * Phase 27 — header pill showing access-window remaining.
 *
 * Variants:
 *   - Annual paid + active     → amber pill   "Annual · N days left"
 *   - Complimentary + active   → sky pill     "Complimentary · N days left"
 *   - Expired or paywall-hit   → muted pill   "Free · upgrade"   (links to /subscribe)
 *   - No subscription block    → renders null (admins / unknown shapes)
 *
 * The pill collapses to "N days" on small screens and reveals the plan + exact
 * expiry date in a tooltip on hover/tap.
 */
const fmtFullDate = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } catch {
        return null;
    }
};

export const SubscriptionPill = ({ user, className }) => {
    if (!user) return null;
    // Admin role still gets a pill IF a subscription block is present —
    // it just won't typically have one, in which case we render null cleanly.
    const sub = user.subscription;
    if (!sub) return null;

    const daysLeft =
        typeof sub.days_remaining === "number"
            ? sub.days_remaining
            : typeof user.expires_in_days === "number"
                ? user.expires_in_days
                : null;
    const status = sub.status;
    const plan = sub.plan;
    const exactExpiry = fmtFullDate(sub.expires_at);

    // -- Variant: active complimentary
    if (status === "active" && plan === "complimentary" && typeof daysLeft === "number") {
        return (
            <PillShell
                className={cn(
                    "border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100",
                    className
                )}
                testId="subscription-pill"
                variant="complimentary"
                icon={<Sparkles className="h-3 w-3" aria-hidden />}
                fullLabel={`Complimentary · ${daysLeft} day${daysLeft === 1 ? "" : "s"} left`}
                shortLabel={`${daysLeft} day${daysLeft === 1 ? "" : "s"}`}
                tooltip={
                    <span>
                        <span className="font-medium">Complimentary access</span>
                        <br />
                        Expires {exactExpiry || "soon"}
                    </span>
                }
            />
        );
    }

    // -- Variant: active annual (paid)
    if (status === "active" && plan && plan !== "complimentary" && typeof daysLeft === "number") {
        // Capitalise plan label safely (e.g. "annual" → "Annual").
        const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1).replace(/_/g, " ");
        return (
            <PillShell
                className={cn(
                    "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100",
                    className
                )}
                testId="subscription-pill"
                variant="annual"
                icon={<CalendarClock className="h-3 w-3" aria-hidden />}
                fullLabel={`${planLabel} · ${daysLeft} day${daysLeft === 1 ? "" : "s"} left`}
                shortLabel={`${daysLeft} day${daysLeft === 1 ? "" : "s"}`}
                tooltip={
                    <span>
                        <span className="font-medium">{planLabel} subscription</span>
                        <br />
                        Expires {exactExpiry || "soon"}
                    </span>
                }
            />
        );
    }

    // -- Variant: expired / free / paywall-hit → clickable upgrade affordance.
    // We surface this only when the backend reports the user IS on the paywall
    // path (status free OR expired). Brand-new "no block at all" users
    // returned null at the top.
    if (status === "free" || status === "expired" || (!plan && status !== "active")) {
        return (
            <Link
                to="/subscribe"
                className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary/80 hover:text-foreground",
                    className
                )}
                data-testid="subscription-pill"
                data-variant="free"
            >
                <Sparkles className="h-3 w-3" aria-hidden />
                <span className="hidden sm:inline">Free · upgrade</span>
                <span className="sm:hidden">Upgrade</span>
            </Link>
        );
    }

    // Unknown shape — fail closed (don't render a confusing pill).
    return null;
};

const PillShell = ({ className, testId, variant, icon, fullLabel, shortLabel, tooltip }) => (
    <TooltipProvider delayDuration={250}>
        <Tooltip>
            <TooltipTrigger asChild>
                <span
                    className={cn(
                        "inline-flex cursor-default items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                        className
                    )}
                    data-testid={testId}
                    data-variant={variant}
                >
                    {icon}
                    <span className="hidden sm:inline" data-testid={`${testId}-full`}>
                        {fullLabel}
                    </span>
                    <span className="sm:hidden" data-testid={`${testId}-short`}>
                        {shortLabel}
                    </span>
                </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs leading-snug">
                {tooltip}
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
);
